import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Icons,
  Tooltip,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
  Modal,
  Button,
  Alert,
} from '@ublend-npm/aulaui-core';
import useIsOverflowing from '@app/hooks/useIsOverflowing';
import { COMMON_ROOM } from '@core/constants/postFeedType';
import { createStyles } from './PostCard.styles';
import { CANCEL, CLOSE, DELETE, COMMON_ROOM_TEXT } from './text';
import type { FeedType, PostCardMenuItem, PostCardProps } from './types';
import EditPostModal from '../PostModal/EditPostModal.container';

interface DeletePostDialogProps {
  feedTypeText: any;
  styles: any;
  open: boolean;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

interface EditPostDialogProps {
  styles: any;
  open: boolean;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
  content: any;
  itemClass: string;
  itemType: FeedType;
  itemId: string;
}

const PostCard = ({
  author,
  timestamp,
  edited = false,
  content,
  media,
  tags,
  reactions,
  comments,
  postActions,
  ariaLabel = 'Post',
  menuItems = [],
  onSelectMenuItem,
  deleteDialogOpen = false,
  onCloseDeleteDialog,
  onConfirmDelete,
  onCloseEditDialog,
  onConfirmEdit,
  isLoading = false,
  isPinnedPost = false,
  feedType,
  editDialogOpen = false,
  itemId,
  postContent,
}: PostCardProps) => {
  const theme = useTheme();
  const styles = createStyles(theme as any);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [authorNameRef, isAuthorNameOverflowing] =
    useIsOverflowing<HTMLDivElement>();

  // Get the text for the post card based on the feed type
  const feedTypeText =
    feedType === COMMON_ROOM ? COMMON_ROOM_TEXT : COMMON_ROOM_TEXT;

  const initials =
    author.initials ||
    (author.firstName?.charAt(0).toUpperCase() || 'U') +
      (author.lastName?.charAt(0).toUpperCase() || 'U');

  const renderSection = (label: string, children: React.ReactNode) => {
    if (!children) return null;

    return (
      <>
        <Box component="hr" sx={styles.divider} />
        <Box component="section" aria-label={label} sx={styles.section}>
          {children}
        </Box>
      </>
    );
  };

  return (
    <Box component="article" aria-label={ariaLabel} sx={styles.card}>
      {feedType === COMMON_ROOM && isPinnedPost && (
        <Alert severity="info" sx={styles.pinnedPostAlert}>
          {feedTypeText.pinnedPost}
        </Alert>
      )}
      <Box component="header" sx={styles.header}>
        <Box sx={styles.author}>
          <Avatar
            altText={author.name}
            fallbackText={initials}
            kind="circular"
            src={author.avatarUrl}
            sx={styles.avatar}
          />
          <Box sx={styles.authorDetails}>
            <Box sx={styles.authorNameRow}>
              <Tooltip
                title={author.name}
                disableFocusListener={!isDesktop || !isAuthorNameOverflowing}
                disableHoverListener={!isDesktop || !isAuthorNameOverflowing}
                disableTouchListener={!isDesktop || !isAuthorNameOverflowing}
              >
                <Box ref={authorNameRef} sx={styles.authorName}>
                  {author.name}
                </Box>
              </Tooltip>
              {author.role ? (
                <Chip label={author.role} size="small" sx={styles.roleBadge} />
              ) : null}
            </Box>
            <Typography sx={styles.timestamp}>{timestamp}</Typography>
          </Box>
        </Box>

        {postActions ||
          (menuItems.length > 0 && onSelectMenuItem ? (
            <ThreeDotMenu
              feedTypeText={feedTypeText}
              styles={styles}
              menuItems={menuItems}
              onSelectMenuItem={onSelectMenuItem}
            />
          ) : null)}
      </Box>
      <DeletePostDialog
        feedTypeText={feedTypeText}
        styles={styles}
        open={deleteDialogOpen}
        loading={isLoading}
        onClose={() => onCloseDeleteDialog?.()}
        onConfirm={() => onConfirmDelete?.()}
      />
      <EditPostDialog
        key={itemId}
        styles={styles}
        open={editDialogOpen}
        loading={isLoading}
        onClose={() => onCloseEditDialog?.()}
        onConfirm={() => onConfirmEdit?.()}
        content={postContent}
        itemClass="UBClassRoomPost"
        itemId={itemId}
        itemType={feedType}
      />
      {content ? <Box sx={styles.content}>{content}</Box> : null}
      {media ? <Box sx={styles.media}>{media}</Box> : null}
      {edited ? (
        <Typography sx={styles.edited}>{feedTypeText.edited}</Typography>
      ) : null}
      {renderSection(feedTypeText.tagsSection, tags)}
      {renderSection(feedTypeText.reactionsSection, reactions)}
      {renderSection(feedTypeText.commentsSection, comments)}
    </Box>
  );
};

export default PostCard;

const ThreeDotMenu = ({
  feedTypeText,
  styles,
  menuItems,
  onSelectMenuItem,
}: {
  feedTypeText: any;
  styles: any;
  menuItems: PostCardMenuItem[];
  onSelectMenuItem: (item: PostCardMenuItem) => void;
}) => {
  const [showOptions, setShowOptions] = useState<HTMLElement | null>(null);

  return (
    <>
      <IconButton
        aria-label={feedTypeText.postOptions}
        size="small"
        sx={styles.options}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          setShowOptions(event.currentTarget);
        }}
      >
        <Icons.MoreVert />
      </IconButton>
      <Menu
        sx={styles.menu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        anchorEl={showOptions}
        open={Boolean(showOptions)}
        onClose={() => {
          setShowOptions(null);
        }}
        onClick={(e) => e.stopPropagation()}
        slotProps={{
          paper: {
            style: {
              width: '160px',
              maxWidth: '160px',
            },
          },
        }}
      >
        {menuItems.map((item) => (
          <MenuItem
            key={item.key}
            label={item.label}
            onClick={() => {
              setShowOptions(null);
              onSelectMenuItem(item);
            }}
          />
        ))}
      </Menu>
    </>
  );
};

const DeletePostDialog: React.FC<DeletePostDialogProps> = ({
  feedTypeText,
  styles,
  open,
  loading = false,
  onClose,
  onConfirm,
}) => {
  return (
    <Modal open={open} onClose={loading ? undefined : onClose}>
      <Box sx={styles.dialog}>
        <Box sx={styles.dialogHeader}>
          <Typography
            sx={styles.title}
            id="delete-assignment-title"
            tabIndex={0}
          >
            {feedTypeText.deletePostTitle}
          </Typography>
          <IconButton
            aria-label={feedTypeText.closeDeleteDialog}
            onClick={onClose}
            sx={styles.closeButton}
            disabled={loading}
          >
            <Tooltip title={CLOSE}>
              <Icons.Close />
            </Tooltip>
          </IconButton>
        </Box>

        <Typography component="div" sx={styles.body} tabIndex={0}>
          {feedTypeText.deletePostConfirmation}
        </Typography>

        <Box sx={styles.footer}>
          <Button
            kind="secondary"
            type="button"
            sx={styles.buttonSx}
            onClick={onClose}
            disabled={loading}
          >
            {CANCEL}
          </Button>
          <Button
            kind="primary"
            type="button"
            sx={styles.buttonSx}
            onClick={onConfirm}
            disabled={loading}
            loading={loading}
          >
            {DELETE}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

const EditPostDialog = ({
  styles,
  open,
  loading = false,
  onClose,
  onConfirm,
  content,
  itemClass,
  itemType,
  itemId,
}: EditPostDialogProps) => {
  return (
    <EditPostModal
      open={open}
      content={content}
      itemClass={itemClass}
      itemType={itemType}
      itemId={itemId}
      onClose={() => {
        onClose();
      }}
    />
  );
};
